import React, { useState } from 'react'
import { Button, Form, Input, Typography } from 'antd'
import { useIntl } from 'react-intl'

import IntlMessages from '../../../../util/IntlMessages'
import { GoogleIcon, SlackIcon, TeamsIcon } from '../../../../components/icons'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { PLATFORM_BRAND } from '../platform-brand'
import { ConnectPlatform, SignupBusySource } from '../types'

const { Paragraph, Link } = Typography

const PLATFORM_BUTTONS: { platform: ConnectPlatform; icon: React.ReactElement; qa: string }[] = [
  { platform: PlatformEnum.Slack, icon: <SlackIcon />, qa: 'slack-signup' },
  { platform: PlatformEnum.Microsoft, icon: <TeamsIcon />, qa: 'microsoft-signup' },
  { platform: PlatformEnum.Google, icon: <GoogleIcon />, qa: 'google-signup' },
]

export interface IConnectStepProps {
  email: string
  /** Which control started the attempt in flight, or `null`. */
  busy: SignupBusySource | null
  /** `null` shows every method. Anything else shows only that one. */
  focusedPlatform: PlatformEnum | null
  onSubmitEmail: (email: string) => void
  onEmailChanged: () => void
  onPlatformSignup: (platform: ConnectPlatform) => void
  onShowAllOptions: () => void
  onLogin: () => void
}

const ConnectStep = ({ email, busy, focusedPlatform, onSubmitEmail, onEmailChanged, onPlatformSignup, onShowAllOptions, onLogin }: IConnectStepProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const [hasTyped, setHasTyped] = useState(false)

  const platformButtons = PLATFORM_BUTTONS.filter(({ platform }) => !focusedPlatform || focusedPlatform === platform)
  const showEmail = !focusedPlatform || focusedPlatform === PlatformEnum.Email
  // Everything is unavailable while an attempt runs, but only the control that started it says
  // so with a spinner.
  const isBusy = busy !== null

  return (
    <>
      {platformButtons.map(({ platform, icon, qa }) => (
        <Button key={platform} size="large" icon={icon} onClick={() => onPlatformSignup(platform)} disabled={isBusy} loading={busy === platform} data-qa={qa} block>
          {formatMessage({ id: 'signup.signUpWithPlatform' }, { platform: PLATFORM_BRAND[platform].short })}
        </Button>
      ))}
      {showEmail && (
        <>
          <Paragraph className="connect-with-email">
            <IntlMessages id="app.email" />
          </Paragraph>
          <Form
            name="connect-with-email"
            form={form}
            layout="horizontal"
            initialValues={{ email }}
            onFinish={(values) => onSubmitEmail(values.email)}
            onFieldsChange={onEmailChanged}
          >
            <Form.Item
              name="email"
              style={{ width: '100%' }}
              // Half a typed address is not a wrong one, and an untouched field is not one
              // either. Ant validates on change by default, which scolds on the first keystroke.
              validateTrigger={hasTyped ? 'onBlur' : []}
              rules={[{ required: true, type: 'email', message: <IntlMessages id="connect.pleaseInputValidEmail" /> }]}
            >
              <Input
                className="email-input data-hj-allow"
                size="large"
                data-qa="email-signup-mail"
                placeholder={formatMessage({ id: 'app.enterWorkEmail' })}
                onChange={() => setHasTyped(true)}
              />
            </Form.Item>
            <Form.Item>
              <Button size="large" type="primary" htmlType="submit" disabled={isBusy} loading={busy === PlatformEnum.Email} data-qa="email-signup-submit" block>
                <IntlMessages id="signup.signUpWithEmail" />
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
      {/* A focused link narrows the page to one method, so it owes the visitor the way back to
          the rest of them. */}
      {focusedPlatform && (
        <Paragraph className="signup-other-options">
          <Link onClick={onShowAllOptions} data-qa="signup-other-options">
            <IntlMessages id="signup.otherSignUpOptions" />
          </Link>
        </Paragraph>
      )}
      <Paragraph className="auth-sidebar-login">
        <IntlMessages id="signup.alreadyHaveAnAccount" />
        &nbsp;
        <Link onClick={onLogin} data-qa="signup-signin-link">
          <IntlMessages id="app.login" />
        </Link>
      </Paragraph>
    </>
  )
}

export default ConnectStep
